import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface Report {
  id: number;
  name: string;
  description: string;
  query: string;
  category: string;
  is_row_report: boolean;
}

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: string;
  rowId?: number | string;
  isRowReport?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  'General': 'Geral',
  'Staff': 'Colaboradores',
  'Parent': 'Pais/Responsáveis',
  'Meeting': 'Reuniões',
  'BibleStudy': 'Estudos Bíblicos',
  'Event': 'Eventos',
  'Incident': 'Ocorrências',
  'Project': 'Projetos',
  'Survey': 'Pesquisas',
  'Content': 'Conteúdos',
  'SchoolClass': 'Turmas',
};

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, category, rowId, isRowReport }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`custom-reports/`, {
        params: { category, is_row_report: isRowReport ? 'true' : 'false' }
      });
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports", error);
    } finally {
      setLoading(false);
    }
  }, [category, isRowReport]);

  useEffect(() => {
    if (isOpen) {
      fetchReports();
      setResults([]);
      setSelectedReport(null);
      setParams({});
    }
  }, [isOpen, fetchReports]);

  const detectParams = (query: string) => {
    const regex = /%\((\w+)\)s/g;
    const found: string[] = [];
    let match;
    while ((match = regex.exec(query)) !== null) {
      if (!found.includes(match[1])) {
        found.push(match[1]);
      }
    }
    return found;
  };

  const handleSelectReport = (report: Report) => {
    setSelectedReport(report);
    const detected = detectParams(report.query);
    const initialParams: Record<string, string> = {};
    detected.forEach(p => {
      if (p === 'id' && rowId) {
        initialParams[p] = String(rowId);
      } else {
        initialParams[p] = '';
      }
    });
    setParams(initialParams);
    setResults([]);
  };

  const handleRun = async () => {
    if (!selectedReport) return;
    setExecuting(true);
    try {
      const response = await api.post(`custom-reports/${selectedReport.id}/run/`, params);
      setResults(response.data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao executar relatório');
    } finally {
      setExecuting(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>${selectedReport?.name}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { color: #333; margin-bottom: 5px; }
            p { margin: 5px 0; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <h1>${selectedReport?.name}</h1>
          <p>${selectedReport?.description}</p>
          <p><small>Gerado em: ${new Date().toLocaleString()}</small></p>
          <table>
            <thead>
              <tr>
                ${Object.keys(results[0]).map(key => `<th>${key.replace(/_/g, ' ').toUpperCase()}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${results.map(row => `
                <tr>
                  ${Object.values(row).map(val => `<td>${val !== null ? val : '-'}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>window.print();</script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportExcel = () => {
    if (results.length === 0) return;

    const headers = Object.keys(results[0]);
    const csvContent = [
      headers.join(';'),
      ...results.map(row => headers.map(header => {
        const value = row[header] !== null ? String(row[header]) : '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedReport?.name || 'relatorio'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const categoryTitle = CATEGORY_LABELS[category] || category;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100
    }}>
      <div className="card" style={{ maxWidth: '1000px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
          <h2 style={{ margin: 0 }}>Relatórios: {categoryTitle} {isRowReport ? '(Individual)' : '(Geral)'}</h2>
          <button className="btn btn-secondary" onClick={onClose} style={{ padding: '5px 10px' }}>&times;</button>
        </div>

        <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0 }}>
          {/* Sidebar: Report List */}
          <div style={{ width: '250px', borderRight: '1px solid var(--border-color)', paddingRight: '15px', overflowY: 'auto' }}>
            {loading ? (
              <p>Carregando relatórios...</p>
            ) : reports.length === 0 ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <p>Nenhum relatório {isRowReport ? 'individual' : 'geral'} encontrado para {categoryTitle}.</p>
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Dica:</p>
                  <p>Verifique no Gerador de Relatórios:</p>
                  <ul style={{ paddingLeft: '20px' }}>
                    <li>Categoria: <b>{categoryTitle}</b></li>
                    <li>Tipo: <b>{isRowReport ? 'Relatório de Linha' : 'Relatório Geral (desmarcado)'}</b></li>
                  </ul>
                </div>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {reports.map(r => (
                  <li 
                    key={r.id} 
                    onClick={() => handleSelectReport(r)}
                    style={{ 
                      padding: '12px', 
                      cursor: 'pointer', 
                      borderRadius: '6px',
                      backgroundColor: selectedReport?.id === r.id ? 'var(--accent-red)' : 'var(--bg-secondary)',
                      color: selectedReport?.id === r.id ? 'white' : 'inherit',
                      marginBottom: '8px',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    {r.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedReport ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: 0 }}>{selectedReport.name}</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '5px' }}>{selectedReport.description}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn" onClick={handleRun} disabled={executing}>
                        {executing ? 'Gerando...' : 'Gerar Relatório'}
                      </button>
                      {results.length > 0 && (
                        <>
                          <button className="btn btn-secondary" onClick={handleExportExcel}>Exportar Excel</button>
                          <button className="btn btn-secondary" onClick={handlePrint}>Imprimir</button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {Object.keys(params).length > 0 && (
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <p style={{ fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '8px' }}>Parâmetros:</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                        {Object.keys(params).map(p => (
                          <div key={p}>
                            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{p}</label>
                            <input 
                              type="text" 
                              value={params[p]} 
                              onChange={e => setParams({...params, [p]: e.target.value})}
                              placeholder={`Valor para ${p}`}
                              style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-secondary)' }}>
                  {results.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1 }}>
                        <tr>
                          {Object.keys(results[0]).map(key => (
                            <th key={key} style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border-color)', textTransform: 'capitalize' }}>
                              {key.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            {Object.values(row).map((val: any, vIdx) => (
                              <td key={vIdx} style={{ padding: '10px' }}>{val !== null ? String(val) : '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      {executing ? 'Aguarde, processando dados...' : 'Configure os parâmetros e clique em Gerar Relatório.'}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <p>Selecione um relatório na lista à esquerda para começar.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
